import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bed, Bath, Users, MapPin, Map, Grid, Loader2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const Properties = () => {
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/properties`);
        if (!response.ok) throw new Error('Failed to fetch properties');
        const data = await response.json();
        // Convert snake_case to camelCase for frontend compatibility
        const formattedData = data.map(p => ({
          ...p,
          pricePerNight: p.price_per_night,
          bookingUrl: p.booking_url,
          bedConfiguration: p.bed_configuration
        }));
        setProperties(formattedData);
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const filteredProperties = filter === 'all' 
    ? properties 
    : filter === 'featured'
    ? properties.filter(p => p.featured)
    : properties.filter(p => p.bedrooms === parseInt(filter));

  // Calculate map center based on all properties
  const mapCenter = [38.91, -77.00]; // DC area center

  return (
    <div className="bg-slate-50 min-h-screen py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Our Properties</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Explore our collection of premium short-term rentals in Washington DC's most desirable neighborhoods.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-slate-600" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-500 mb-4">Failed to load properties. Please try again.</p>
            <Button onClick={() => window.location.reload()} className="bg-slate-800">Retry</Button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* View Toggle */}
            <div className="flex justify-center gap-2 mb-10">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-slate-800 text-white' : 'border-slate-300 text-slate-700'}
                size="sm"
              >
                <Grid className="h-4 w-4 mr-2" />
                Grid View
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' ? 'bg-slate-800 text-white' : 'border-slate-300 text-slate-700'}
                size="sm"
              >
                <Map className="h-4 w-4 mr-2" />
                Map View
              </Button>
            </div>

            {/* Map View */}
            {viewMode === 'map' && (
          <div className="mb-10 rounded-lg overflow-hidden shadow-lg border border-slate-200">
            <MapContainer 
              center={mapCenter} 
              zoom={12} 
              style={{ height: '500px', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {filteredProperties.map((property) => (
                property.coordinates && (
                  <Marker 
                    key={property.id} 
                    position={[property.coordinates.lat, property.coordinates.lng]}
                  >
                    <Popup>
                      <div className="p-1 min-w-[200px]">
                        <img 
                          src={property.images[0]} 
                          alt={property.name}
                          className="w-full h-24 object-cover rounded mb-2"
                        />
                        <h3 className="font-bold text-sm">{property.name}</h3>
                        <p className="text-xs text-slate-600 mb-1">{property.location}</p>
                        <p className="text-sm font-semibold text-slate-800">${property.pricePerNight}/night</p>
                        <div className="flex gap-2 text-xs text-slate-600 mt-1">
                          <span>{property.bedrooms} Bed</span>
                          <span>•</span>
                          <span>{property.bathrooms} Bath</span>
                          <span>•</span>
                          <span>{property.sleeps} Guests</span>
                        </div>
                        <Link to={`/property/${property.id}`}>
                          <Button size="sm" className="w-full mt-2 bg-slate-800 hover:bg-slate-700 text-white text-xs">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                )
              ))}
            </MapContainer>
          </div>
        )}

        {/* Properties Grid */}
        {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProperties.map((property) => (
            <Card key={property.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-slate-200 bg-white">
              <div className="relative h-64 overflow-hidden bg-slate-200">
                <img
                  src={property.images[0]}
                  alt={property.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    console.error('Image failed to load:', property.images[0]);
                  }}
                />
                {property.featured && (
                  <Badge className="absolute top-4 left-4 bg-slate-800 text-white">
                    Featured
                  </Badge>
                )}
                <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full shadow-md">
                  <span className="text-sm font-semibold text-slate-800">${property.pricePerNight}/night</span>
                </div>
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{property.name}</h3>
                <div className="flex items-center text-sm text-slate-600 mb-4">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span>{property.location}</span>
                </div>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">{property.description}</p>
                <div className="flex items-center justify-between text-sm text-slate-600 mb-4 pb-4 border-b border-slate-200">
                  <div className="flex items-center">
                    <Bed className="h-4 w-4 mr-1" />
                    <span>{property.bedrooms} Bed</span>
                  </div>
                  <div className="flex items-center">
                    <Bath className="h-4 w-4 mr-1" />
                    <span>{property.bathrooms} Bath</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    <span>{property.sleeps} Guests</span>
                  </div>
                </div>
                <Link to={`/property/${property.id}`}>
                  <Button className="w-full bg-slate-800 hover:bg-slate-700 text-white">
                    View Details & Book
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
        )}

        {/* No Results */}
        {!loading && filteredProperties.length === 0 && (
          <div className="text-center py-12">
            <p className="text-lg text-slate-600">No properties found with the selected filter.</p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-white rounded-lg p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Can't Find What You're Looking For?</h2>
          <p className="text-slate-600 mb-6">
            Contact us directly and we'll help you find the perfect property for your stay.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-slate-800 hover:bg-slate-700 text-white">
              Contact Us
            </Button>
          </Link>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default Properties;